import React, { createContext, useState, useContext, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Cookies from 'js-cookie';

declare module '@mui/material/styles' {
  interface Palette {
    status: {
      accent: string;
      complete: string;
      pending: string;
      retrying: string;
      error: string;
      notStarted: string;
    };
  }

  interface PaletteOptions {
    status?: {
      accent?: string;
      complete?: string;
      pending?: string;
      retrying?: string;
      error?: string;
      notStarted?: string;
    };
  }
}

interface IThemeContext {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<IThemeContext | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const UMS_ACCENT_RED = '#c62828';
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    return Cookies.get('darkMode') === 'true';
  });

  const toggleDarkMode = () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    Cookies.set('darkMode', newValue.toString(), { expires: 365 });
  };

  // Memoize the theme to prevent unnecessary recalculations on every render
  const theme = useMemo(() => createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#000' : '#fff',
      },
      action: {
        selected: darkMode ? '#3c3c3c' : '#fff',
      },
      background: {
        default: darkMode ? '#333' : '#ddd',
        paper: darkMode ? '#000' : '#fff',
      },
      text: {
        primary: darkMode ? '#fff' : '#111',
      },
      status: {
        accent: UMS_ACCENT_RED,
        complete: '#4caf50',   // Vibrant Green (like default MUI success)   // Vibrant Green
        pending: '#fdd835',
        retrying: '#f57c00',   // Bright Orange (a bit deeper than pending)
        error: '#f44336',      // Vibrant Red (like default MUI error)
        notStarted: '#9e9e9e', // Lighter, neutral Grey
      },
    },
    components: {
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--ums-accent-red': UMS_ACCENT_RED,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: darkMode ? alpha('#fff', 0.72) : alpha('#000', 0.65),
            '&.Mui-focused': {
              color: UMS_ACCENT_RED,
            },
            '&.Mui-disabled': {
              color: darkMode ? alpha('#fff', 0.42) : alpha('#000', 0.38),
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: darkMode ? '#fff' : '#111',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? alpha('#fff', 0.24) : alpha('#000', 0.23),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? alpha('#fff', 0.45) : alpha('#000', 0.45),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: UMS_ACCENT_RED,
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? alpha('#fff', 0.18) : alpha('#000', 0.18),
            },
          },
          input: {
            '&::placeholder': {
              opacity: 1,
              color: darkMode ? alpha('#fff', 0.48) : alpha('#000', 0.45),
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: darkMode ? alpha('#fff', 0.9) : alpha('#000', 0.87),
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: darkMode ? alpha('#fff', 0.7) : alpha('#000', 0.54),
            '&.Mui-checked': {
              color: UMS_ACCENT_RED,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: darkMode ? alpha('#fff', 0.35) : alpha('#000', 0.28),
          },
          switchBase: {
            '&.Mui-checked': {
              color: UMS_ACCENT_RED,
            },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: UMS_ACCENT_RED,
              opacity: 0.9,
            },
          },
        },
      },
    },
  }), [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};
